import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { forEach, isEqual, noop, isEmpty, get } from 'lodash-es';

import { calcDistance, calcCenter, between } from './map-helpers';

import {
  STATES_DATA,
  DEFAULT_LABELS,
  DEFAULT_LABELS_WITH_CONNECTORS,
  DYNAMIC_LABELS,
  DYNAMIC_LABELS_WITH_CONNECTORS,
} from './map-data';

import './us-map.scss';

const MAP_WIDTH = 757;
const MAP_HEIGHT = 463;
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
const RESOLUTION = MAP_WIDTH / MAP_HEIGHT;

// Bounds for translate. Numbers are gotten empirically.
const MIN_LEFT = -1025;
const MAX_LEFT = 80;
const MIN_TOP = -600;
const MAX_TOP = 20;

export class USMap extends Component {
  static propTypes = {
    onMapStateClick: PropTypes.func,
    isMobile: PropTypes.bool,
    statesColorMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    statesLabelMap: PropTypes.objectOf(PropTypes.string),
    hasDynamicLabels: PropTypes.bool,
    isClickable: PropTypes.bool,
    strokeColor: PropTypes.string,
    defaultColor: PropTypes.string,
    labelsColor: PropTypes.string,
  };

  static defaultProps = {
    isMobile: false,
    statesColorMap: {},
    onMapStateClick: noop,
    statesLabelMap: null,
    hasDynamicLabels: false,
    isClickable: false,
    strokeColor: '#FFFFFF',
    defaultColor: '#999999',
    labelsColor: '',
  };

  state = {
    zoom: 1,
    left: 0,
    top: 0,
  };

  componentDidMount() {
    this.setStatesColors();
    this.setStatesLabels();
  }

  componentDidUpdate(prevProps) {
    const { statesColorMap, statesLabelMap } = this.props;

    if (!isEqual(prevProps.statesColorMap, statesColorMap)) {
      this.setStatesColors();
    }

    if (!isEqual(prevProps.statesLabelMap, statesLabelMap)) {
      this.setStatesLabels();
    }
  }

  onTouchStart = e => {
    e.preventDefault();

    this.setState({ isTapping: true });
  };

  onTouchMove = e => {
    this.setState({ isTapping: false });

    document.body.classList.add('map-moving');

    const touches = e.touches;
    const length = touches.length;
    if (length === 1) {
      const [{ clientX, clientY }] = touches;
      this.processTouch(clientX, clientY);
    } else if (length === 2) {
      const [touch1, touch2] = touches;
      this.processPinch(touch1.clientX, touch1.clientY, touch2.clientX, touch2.clientY);
    }
  };

  onTouchEnd = e => {
    const { isTapping } = this.state;

    e.preventDefault();

    if (isTapping) {
      this.onMapClick(e);
    } else {
      document.body.classList.remove('map-moving');
    }

    this.setState({
      isZooming: false,
      isMoving: false,
      isTapping: false,
    });
  };

  onMapClick = e => {
    e.preventDefault();

    const target = e.target;

    if (target.dataset) {
      this.props.onMapStateClick(target.dataset.value);
    } else {
      // IE support
      const stateId = target.textContent || target.id;
      if (stateId) {
        this.props.onMapStateClick(stateId);
      }
    }
  };

  setStatesColors = () => {
    const { statesColorMap } = this.props;
    forEach(statesColorMap, (statesList, color) => {
      statesList.forEach(state => {
        this.fillStateWithColor(state, color);
      });
    });
  };

  setStatesLabels = () => {
    const { statesLabelMap, hasDynamicLabels } = this.props;

    if (!hasDynamicLabels || isEmpty(statesLabelMap)) return;

    forEach(statesLabelMap, (label, stateCode) => {
      let labelParts = label.split(' ');

      const stateLabelEl = document.querySelector(`.us-map text[data-value="${stateCode}"]`);
      if (stateLabelEl) {
        const labelData = DYNAMIC_LABELS[stateCode] || DYNAMIC_LABELS_WITH_CONNECTORS[stateCode];
        const { x: xOffset, isSmall } = labelData;
        const isOuterLabel = !!DYNAMIC_LABELS_WITH_CONNECTORS[stateCode];

        if (!isOuterLabel && isSmall && labelParts.length > 2) {
          labelParts = labelParts.slice(0, 2);
          labelParts[1] = `${labelParts[1]}...`;
        } else if (labelParts.length === 3) {
          const [firstWord, secondWord, thirdWord] = labelParts;
          if (secondWord.length < thirdWord.length) {
            labelParts = [`${firstWord} ${secondWord}`, thirdWord];
          } else {
            labelParts = [firstWord, `${secondWord} ${thirdWord}`];
          }
        }
        const linePadding = isSmall ? '11' : '14';

        stateLabelEl.innerHTML = labelParts
          .map((part, index) => {
            const stateFullName =
              get(DYNAMIC_LABELS, `${stateCode}.fullName`) ||
              get(DYNAMIC_LABELS_WITH_CONNECTORS, `${stateCode}.fullName`);
            const stateDesc =
              index === 0 ? `<tspan class="state-name-desc-for-screen-reader">${stateFullName}</tspan>` : '';
            return `${stateDesc}<tspan x=${xOffset} dy=${index === 0 ? '0' : linePadding}>${part}</tspan>`;
          })
          .join('');
      }
    });
  };

  // eslint-disable-next-line class-methods-use-this
  fillStateWithColor = (stateCode, color) => {
    const stateElements = document.querySelectorAll(`.us-map #${stateCode} .state-body`);
    if (stateElements && stateElements.length) {
      stateElements.forEach(el => {
        // eslint-disable-next-line no-param-reassign
        el.style.fill = color;
      });
    }
  };

  processPinch = (x1, y1, x2, y2) => {
    const distance = calcDistance(x1, y1, x2, y2);
    const { x, y } = calcCenter(x1, y1, x2, y2);

    if (!this.state.isZooming) {
      const { top, left, zoom } = this.state;

      this.setState({
        isZooming: true,
        initialX: x,
        initialY: y,
        initialTop: top,
        initialLeft: left,
        initialZoom: zoom,
        initialDistance: distance,
      });
    } else {
      const { initialZoom, initialDistance, initialLeft, initialTop, initialX, initialY } = this.state;

      const touchZoom = distance / initialDistance;
      const zoom = between(MIN_ZOOM, MAX_ZOOM, initialZoom * touchZoom);

      const dx = x - initialX;
      const dy = y - initialY;

      const left = (initialLeft + dx - x) * touchZoom + x;
      const top = (initialTop + dy - y) * touchZoom + y;

      // Do not move map when zoom is at min/max value
      if (zoom === MIN_ZOOM || zoom === MAX_ZOOM) {
        this.setState({
          zoom,
          left: zoom === MIN_ZOOM ? 0 : this.state.left,
          top: zoom === MIN_ZOOM ? 0 : this.state.top,
        });
      } else {
        this.setState({
          zoom,
          left: between(MIN_LEFT, MAX_LEFT, left),
          top: between(MIN_TOP, MAX_TOP, top),
        });
      }
    }
  };

  processTouch = (x, y) => {
    if (!this.state.isMoving || this.state.isZooming) {
      const { top, left } = this.state;
      this.setState({
        isMoving: true,
        isZooming: false,
        initialLeft: left,
        initialTop: top,
        initialX: x,
        initialY: y,
      });
    } else {
      const { initialX, initialY, initialLeft, initialTop, zoom } = this.state;

      // Disable moving when map is not scaled
      if (zoom === 1) {
        return;
      }

      const dx = x - initialX;
      const dy = y - initialY;

      const left = initialLeft + dx;
      const top = initialTop + dy;

      this.setState({
        left: between(MIN_LEFT, MAX_LEFT, left),
        top: between(MIN_TOP, MAX_TOP, top),
      });
    }
  };

  render() {
    const { isMobile, hasDynamicLabels, isClickable, strokeColor, defaultColor, labelsColor, statesColorMap } =
      this.props;
    const { left, top, zoom } = this.state;

    const whiteTextStates = new Set(
      Array.prototype.concat(get(statesColorMap, '#534C4C', []), get(statesColorMap, '#000000', []))
    );

    const labels = hasDynamicLabels ? DYNAMIC_LABELS : DEFAULT_LABELS;
    const labelsWithConnectors = hasDynamicLabels ? DYNAMIC_LABELS_WITH_CONNECTORS : DEFAULT_LABELS_WITH_CONNECTORS;

    return (
      <svg
        viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
        onClick={isMobile ? null : this.onMapClick}
        onTouchStart={this.onTouchStart}
        onTouchMove={this.onTouchMove}
        onTouchEnd={this.onTouchEnd}
        className={classnames('us-map', { 'dynamic-labels': hasDynamicLabels, clickable: isClickable })}
        preserveAspectRatio="xMinYMin meet"
      >
        <g
          stroke={strokeColor}
          fill={defaultColor}
          transform={`translate(${left * RESOLUTION} ${top * RESOLUTION}) scale(${zoom})`}
        >
          {STATES_DATA.map(data => (
            <g id={data.id} className="state" data-value={data.id} key={data.id} aria-live="off">
              <path d={data.d} className="state-body" data-value={data.id} aria-live="off" />
            </g>
          ))}
          {Object.values(labels).map(data => (
            <text
              className={classnames('state-label', { small: data.isSmall })}
              fill={whiteTextStates.has(data.id) ? '#FFFFFF' : labelsColor || data.color}
              data-value={data.id}
              key={data.id}
              x={data.x}
              y={data.y}
              aria-live="off"
            >
              <tspan x={data.x} y={data.y} data-value={data.id} aria-live="off">
                {data.id}
              </tspan>
            </text>
          ))}
          {Object.values(labelsWithConnectors).map(data => (
            <g transform={data.transform} key={data.id} data-value={data.id}>
              <text
                className={classnames('state-label', { small: data.isSmall })}
                fill={data.color}
                data-value={data.id}
                x={data.x}
                y={data.y}
                aria-live="off"
              >
                <tspan x={data.x} y={data.y} data-value={data.id} aria-live="off">
                  {data.id}
                </tspan>
              </text>
              {!!data.points && <polyline className="label-connector" points={data.points} />}
              {!!(data.x1 && data.x2 && data.y1 && data.y2) && (
                <line x1={data.x1} y1={data.y1} x2={data.x2} y2={data.y2} className="label-connector" aria-live="off" />
              )}
            </g>
          ))}
        </g>
      </svg>
    );
  }
}
